/**
 * Version_Footer label formatter.
 *
 * Pure, React-independent helper that turns the runtime `version` and
 * `environment` values into structured display parts for the Version_Footer.
 * Owns all display logic, including the production-hiding rule (Req 2.2, 2.3).
 * Never performs I/O and never throws on missing/null input (Req 1.4, 6.1-6.4).
 */

export interface VersionLabelParts {
  /** e.g. "v0.1.0", or null when version is missing/empty/whitespace. */
  versionText: string | null;
  /** e.g. "test" (original casing preserved), or null when hidden/missing. */
  envText: string | null;
}

/** A value is "missing" when absent, null, empty, or whitespace-only (Req 6.1). */
function isMissing(value?: string | null): boolean {
  return value == null || value.trim() === '';
}

/**
 * Compose the Version_Footer display parts from runtime values.
 * Pure: no I/O, no React, no throwing on missing input.
 */
export function formatVersionLabel(
  version?: string | null,
  environment?: string | null,
): VersionLabelParts {
  // versionText: raw non-empty version prefixed with "v" (no trimming of the
  // version itself beyond the missing-check); null when missing.
  const versionText = isMissing(version) ? null : `v${version}`;

  // envText: null when missing; null when trimmed+lowercased === 'prod'
  // (prod hides the env); otherwise the trimmed value with original casing.
  let envText: string | null = null;
  if (!isMissing(environment)) {
    const trimmed = (environment as string).trim();
    envText = trimmed.toLowerCase() === 'prod' ? null : trimmed;
  }

  return { versionText, envText };
}
